/* eslint-disable @typescript-eslint/camelcase */
// @flow
/* eslint indent:0 */
/* eslint-env browser */
/* globals $Keys */
import Stars from '../vote/stars'
import React, { Component } from 'react'
import Question from './question.js'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/button.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import './style/styles.css'
import type { SemanticUIColors } from './semanticUIColors'

type MessageList = { [key: string]: string | Array<string> | MessageList }

/**
 * Translatable messages.
 */
const MESSAGES = {
  de: {
    answerText:
      'Die meisten Leserinnen und Leser bewerten den Beitrag als sehr gut.' +
      ' Das freut uns.',
    questions: ['Wie bewerten Sie diesen Beitrag?'],
    title: '',
    feedback: {
      thanks:
        'Vielen Dank für Ihre Bewertung. Das sagt die Jury: «Super duper Beitrag: 5 Sterne»',
      stats: ''
    },
    error: {
      noSave:
        'Vielen Dank für Ihre Bewertung! Leider gab es ein Problem' +
        ' mit der Kommunikation mit der Datenbank, und Ihre Bewertung konnte' +
        ' nicht gespeichert werden. Bitte versuchen Sie es' +
        ' später erneut.',
      noDownload:
        'Vielen Dank für Ihre Stimme. Leider konnten die' +
        ' Bewertungen der anderen Leserinnen und Leser nicht' +
        ' heruntergeladen werden. Wir können Ihnen deshalb im Moment' +
        ' die durchschnittliche Bewertung nicht anzeigen.'
    },
    submit: 'Bewerten'
  }
}

export type Props = {
  className: string,
  inverted: boolean,
  locale: $Keys<typeof MESSAGES>,
  questions?: Array<string>,
  starAmount: number,
  starColor: SemanticUIColors,
  voteHandler: Function,
  challenge?: string,
  name?: string,
  juryText?: string,
  juryStars?: string,
  average: number
}

type State = {
  averageVotes?: Array<number>,
  errorText?: string,
  hasError: boolean,
  numberOfParticipants?: number,
  overallAverage?: number,
  personalVotes: Array<number>,
  readyToSubmit: boolean,
  votingIsOver: boolean
}

/**
 * Allows people to rate the standalone on various
 * questions.
 *
 * @example
 * <UserFeedback
 *   articleId='20919286'
 *   starColor='yellow'
 *   title='Wie bewerten Sie diese Geschichte?'
 *   answerText='Die meisten Leserinnen und Leser bewerten den Artikel als sehr
 *               gut. Das freut uns.'
 *   questions={['Wie lehrreich war der Beitrag?', 'Wie unterhaltsam war er?',
 *               'Wie gefällt Ihnen die Gestaltung?']}
 * />
 *
 */

export class UserFeedback extends Component<Props, State> {
  static defaultProps = {
    className: '',
    inverted: false,
    locale: 'de',
    starAmount: 5,
    starColor: 'yellow'
  }

  constructor(props: Props) {
    super(props)

    let questions = props.questions || MESSAGES[props.locale].questions

    // Set up the state
    this.state = {
      // Fill the personalVotes with -1 matching the amount of questions
      personalVotes: questions.map(() => -1),
      readyToSubmit: false,
      hasError: false,

      // read the local storage item and convert it to a boolean
      // votingIsOver: !!localStorage.getItem(
      //   'vote-' + this.props.challenge + '-' + this.props.name
      // ),
      votingIsOver: true
    }
  }

  // region Handlers

  /**
   * Write the data after clicking submit button
   * @private
   */
  submitHandler = () => {
    this.props
      .voteHandler(
        this.state.personalVotes[0],
        this.props.challenge,
        this.props.name
      )
      .then(error => {
        if (error) {
          throw new Error(error)
        } else {
          localStorage.setItem(
            'vote-' + this.props.challenge + '-' + this.props.name,
            'true'
          )
        }
        this.setState({
          votingIsOver: true
        })
      })
      .catch(e => {
        console.log('error', e)
        this.setState({
          votingIsOver: true,
          hasError: true,
          errorText: 'error.noSave'
        })
      })
  }

  /** Set the state for all questions – with ratings from Question component
   * @private
   */
  ratingHandler = (question: string, rating: number) => {
    this.setState(state => {
      // Get the old value
      let questionRatings = state.personalVotes
      let questions =
        this.props.questions || MESSAGES[this.props.locale].questions

      // We assume that the ratings are in the same order as the questions. So
      // we look up the index of the question and use that index to set the
      // selected rating
      questionRatings[questions.indexOf(question)] = rating

      return {
        personalVotes: questionRatings
      }
    }, this.checkIfSubmitReady)
  }

  // endregion

  /**
   * Run a check to see whether the submit button should be enabled.
   * @private
   */
  checkIfSubmitReady() {
    this.setState(state => {
      // If every value is above 0, we are ready to submit
      return { readyToSubmit: state.personalVotes.every(value => value > 0) }
    })
  }
  // Region React Lifecycle Methods

  render() {
    /**
     * Returns a translated string – or the prop when it's defined
     * @param key
     * @returns {string|Array<string>}
     * @private
     */
    const t = (key: string): string => {
      return (
        this.props[key] ||
        key
          .split('.')
          .reduce(
            (messageList, prop: string) =>
              messageList.hasOwnProperty(prop)
                ? messageList[prop]
                : 'TRANSLATED_STRING_NOT_FOUND: ' + key,
            MESSAGES[this.props.locale]
          )
      )
    }
    return (
      <div
        className={`user-feedback interactive ${this.props.className} ${
          this.props.inverted ? 'inverted' : ''
          }`}
      >
        {this.state.votingIsOver &&
          this.state.hasError &&
          this.state.errorText && (
            <div className={'feedback-text'}>{t(this.state.errorText)}</div>
          )}
        {!this.state.votingIsOver && (
          <div
            className={`ui small interactive ${
              this.props.inverted ? 'inverted' : ''
              } header`}
          >
            {t('title')}
          </div>
        )}
        {!this.state.hasError && (
          <table
            id='vote-table'
            className={`ui interactive very basic ${
              this.props.inverted ? 'inverted' : ''
              } table`}
          >
            <tbody>
              {t('questions').map((question, i) => {
                return (
                  <Question
                    ratingHandler={this.ratingHandler}
                    starAmount={this.props.starAmount}
                    starColor={this.props.starColor}
                    question={question}
                    votingIsOver={this.state.votingIsOver}
                    averageRating={this.props.average}
                    ownRatingStarAmount={this.state.personalVotes[i]}
                    inverted={this.props.inverted}
                    key={`question-${i}`}
                  />
                )
              })}
            </tbody>
          </table>
        )}
        {this.state.votingIsOver && !this.state.hasError && (
          <div className={'feedback-text'}>
            <span>
              <span className={'static-text'}>Das sagt die Jury:</span>{' '}
              {this.props.juryText}{' '}
              <span className='jury-stars'>
                <Stars averageRating={this.props.juryStars} votingIsOver standalone />
                <p>
                <em>(Die Jury-Wertung macht 20 Prozent, die Publikumswertung 80 Prozent des Resultats aus.)</em>
                </p>
              </span>
            </span>{' '}
          </div>
        )}
        <div
          className={`ui vertical very fitted center aligned ${
            this.props.inverted ? 'inverted' : ''
            } basic segment`}
        >
          {!this.state.votingIsOver && (
            <button
              onClick={this.submitHandler}
              className={`ui button ${
                this.state.readyToSubmit === true ? '' : 'disabled'
                } ${this.props.inverted ? 'inverted' : ''} ${
                this.props.starColor
                }`}
            >
              {t('submit')}
            </button>
          )}
        </div>
      </div>
    )
  }

  // endregion
}

export default UserFeedback
